import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import DOMPurify from 'dompurify';
import { Spin, Button, message } from 'antd';
import { DownloadOutlined, ExpandOutlined, StarOutlined, RobotOutlined } from '@ant-design/icons';
import templateService from 'services/templateService';

/**
 * TemplateRenderer - Securely renders HTML templates in iframe
 * Features: DOMPurify sanitization, caching, export, fullscreen
 */
const TemplateRenderer = ({ templateId, reportData, onFeedback, controls, targetTab, hideHeader = false }) => {
  const [loading, setLoading] = useState(false);
  const [htmlContent, setHtmlContent] = useState('');
  const [metadata, setMetadata] = useState(null);
  const [error, setError] = useState(null);
  const iframeRef = useRef(null);

  useEffect(() => {
    if (templateId && reportData) {
      generateReport();
    }
  }, [templateId, reportData]);

  // Handle cross-context tab switching (calling into iframe)
  useEffect(() => {
    if (iframeRef.current && targetTab && htmlContent) {
      const iframe = iframeRef.current;
      const applyTab = () => {
        try {
          if (iframe.contentWindow && typeof iframe.contentWindow.switchTab === 'function') {
            iframe.contentWindow.switchTab(targetTab);
          }
        } catch (e) {
          console.warn('[TemplateRenderer] Could not switch tab:', e);
        }
      };

      // Apply on load and also immediately if already loaded
      iframe.addEventListener('load', applyTab);
      applyTab();
      return () => iframe.removeEventListener('load', applyTab);
    }
  }, [targetTab, htmlContent]);

  const generateReport = async () => {
    // ... same as before ...
    setLoading(true);
    setError(null);

    try {
      const response = await templateService.renderTemplate(
        templateId,
        reportData
      );

      if (response.success) {
        // Relaxed sanitization for trusted backend templates to ensure scripts like switchTab work
        const sanitizedHtml = DOMPurify.sanitize(response.data.content, {
          ADD_TAGS: ['script', 'style', 'iframe', 'svg', 'path', 'circle', 'line'], 
          ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling', 'onclick', 'viewBox', 'd', 'fill', 'stroke', 'stroke-width', 'cx', 'cy', 'r', 'stroke-dasharray'],
          WHOLE_DOCUMENT: true,
          FORCE_BODY: false,
        });

        const interactiveScript = `
        <script>
            (function() {
                console.log('[TemplateRenderer] Internal Template Script Initialized');
                
                // Tab Switching Implementation
                window.switchTab = function(tab) {
                    const vTab = document.getElementById('visual-tab');
                    const eTab = document.getElementById('exec-tab');
                    const btns = document.querySelectorAll('.tab-btn');
                    if (vTab && eTab) {
                        if (tab === 'visual') {
                            vTab.classList.remove('hidden');
                            eTab.classList.add('hidden');
                            if (btns[0]) btns[0].classList.add('active');
                            if (btns[1]) btns[1].classList.remove('active');
                        } else {
                            vTab.classList.add('hidden');
                            eTab.classList.remove('hidden');
                            if (btns[1]) btns[1].classList.add('active');
                            if (btns[0]) btns[0].classList.remove('active');
                        }
                    }
                };

                // Toggle Mode (Drawer vs Wide) Implementation
                const toggleBtn = document.getElementById('toggleMode');
                if (toggleBtn) {
                    toggleBtn.onclick = function() {
                        const isWide = document.body.classList.contains('wide-mode');
                        if (isWide) {
                            document.body.classList.remove('wide-mode');
                            document.body.classList.add('drawer-mode');
                            toggleBtn.innerText = "Switch to Wide Mode";
                            window.parent.postMessage({ type: 'toggle-width', expanded: false }, '*');
                            window.switchTab('visual');
                        } else {
                            document.body.classList.remove('drawer-mode');
                            document.body.classList.add('wide-mode');
                            toggleBtn.innerText = "Switch to Drawer Mode";
                            window.parent.postMessage({ type: 'toggle-width', expanded: true }, '*');
                        }
                    };
                }

                // Global Regenerate Implementation
                window.regenerateDigest = function() {
                    window.parent.postMessage({ type: 'regenerate' }, '*');
                };
            })();
        </script>
        `;

        let finalHtml = sanitizedHtml;
        if (finalHtml.includes('</body>')) {
            finalHtml = finalHtml.replace('</body>', interactiveScript + '</body>');
        } else {
            finalHtml = finalHtml + interactiveScript;
        }

        setHtmlContent(finalHtml);
        setMetadata({
          templateName: response.data.templateName || 'Template Report',
          generatedAt: response.data.generatedAt || new Date().toISOString(),
          duration: response.data.duration || 0,
          fromCache: response.data.fromCache || false,
        });
      } else {
        setError('Failed to generate report');
      }
    } catch (err) {
      console.error('Report generation error:', err);
      setError(err.message || 'An error occurred while generating the report');
    } finally {
      setLoading(false);
    }
  };

  // ... handleExport, handleFullscreen, handleFeedback ...
  const handleExport = () => {
    if (!htmlContent) {
      message.warning('No content to export');
      return;
    }

    try {
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${metadata?.templateName || 'report'}_${Date.now()}.html`;
      link.click();
      URL.revokeObjectURL(url);
      message.success('Report exported successfully');
    } catch (err) {
      console.error('Export error:', err);
      message.error('Failed to export report');
    }
  };

  const handleFullscreen = () => {
    if (iframeRef.current?.requestFullscreen) {
      iframeRef.current.requestFullscreen();
    } else if (iframeRef.current?.webkitRequestFullscreen) {
      iframeRef.current.webkitRequestFullscreen();
    } else if (iframeRef.current?.msRequestFullscreen) {
      iframeRef.current.msRequestFullscreen();
    }
  };

  const handleFeedback = () => {
    if (onFeedback && metadata) {
      onFeedback(metadata);
    }
  };

  if (loading) {
    return (
      <LoadingContainer>
        <Spin size="large" tip="Generating report..." />
        {metadata?.fromCache && (
          <CacheIndicator>Data retrieved from cache</CacheIndicator>
        )}
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <ErrorContainer>
        <ErrorIcon>⚠️</ErrorIcon>
        <ErrorMessage>{error}</ErrorMessage>
        <Button onClick={generateReport} type="primary">
          Retry Generation
        </Button>
      </ErrorContainer>
    );
  }

  if (!htmlContent) {
    return (
      <EmptyContainer>
        <EmptyMessage>No template content available</EmptyMessage>
      </EmptyContainer>
    );
  }

  return (
    <Container>
      {!hideHeader && (
        <ActionBar>
          <LeftSection>
            {controls && <ControlsWrapper>{controls}</ControlsWrapper>}
            <StatusSection>
              {metadata?.fromCache && (
                <CacheBadge>⚡ Cached</CacheBadge>
              )}
              <Timestamp>Generated at {new Date(metadata?.generatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</Timestamp>
            </StatusSection>
          </LeftSection>

          <ButtonGroup>
            <Button
              icon={<ExpandOutlined />}
              onClick={handleFullscreen}
              size="small"
              type="text"
            />
            <Button 
              icon={<DownloadOutlined />} 
              onClick={handleExport} 
              size="small"
              type="text"
            />
            <Button
              type="primary"
              icon={<StarOutlined />}
              onClick={handleFeedback}
              size="small"
              style={{ borderRadius: '4px', fontSize: '12px' }}
            >
              Rate
            </Button>
          </ButtonGroup>
        </ActionBar>
      )}

      <IframeContainer style={{ padding: hideHeader ? 0 : '0' }}>
        <iframe
          ref={iframeRef}
          srcDoc={htmlContent}
          sandbox="allow-same-origin allow-scripts allow-popups"
          title="Template Report"
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            borderRadius: hideHeader ? '0' : '4px',
            background: 'white'
          }}
        />
      </IframeContainer>

    </Container>
  );
};


const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
  overflow: hidden;
`;

const ActionBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
  border-bottom: 1px solid #f0f0f0;
  background: #fff;
  z-index: 10;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const ControlsWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const StatusSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  border-left: 1px solid #eee;
  padding-left: 16px;
`;

const Timestamp = styled.span`
  font-size: 11px;
  color: #8c8c8c;
  font-style: italic;
`;

const CacheBadge = styled.div`
  padding: 2px 8px;
  background: #f6ffed;
  color: #52c41a;
  border: 1px solid #b7eb8f;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const IframeContainer = styled.div`
  flex: 1;
  overflow: hidden;
  padding: 0;
  background: #fff;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 400px;
  gap: 16px;
`;

const CacheIndicator = styled.div`
  color: #52c41a;
  font-size: 14px;
  font-weight: 500;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 400px;
  gap: 16px;
  background: #fff2e8;
  border: 1px solid #ffbb96;
  border-radius: 8px;
  padding: 32px;
`;

const ErrorIcon = styled.div`
  font-size: 48px;
`;

const ErrorMessage = styled.div`
  font-size: 16px;
  color: #d4380d;
  text-align: center;
`;

const EmptyContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
  background: #fafafa;
  border-radius: 8px;
`;

const EmptyMessage = styled.div`
  font-size: 16px;
  color: #8c8c8c;
`;

export default TemplateRenderer;
